import { defineStore } from 'pinia';
import jwtInterceptor from "@/plugins/jwtInterceptor";
import { AxiosError } from "axios";
import { emitter } from "@/main";

export interface ReoccurringTaskTemplate {
    id: number;
    name: string;
    reoccuringType: number;
    standardTime: string; // ISO 8601 date-time string
    reportBackRequired: boolean;
}

export interface Assignee {
    id: number;
    usersign: string;
    roles: string[];
    status: number;
    isShowFullFirstNameRequired: boolean;
    isExportIcalHolidayEntryRequired: boolean;
    isPushNotificationOnlyDuringClockedInHours: boolean;
    numberOfMinutesReminderBeforeWorkStarts: number | null;
    authenticationAlias: string;
    isESignSet: boolean;
    Firstname: string;
    Lastname: string;
    Workgroup: string;
    Homebase: string;
    Email: string;
    Phonenumber: string | null;
    CustomUserImage: string;
    CustomPhoneImage: string;
    CustomCarImage: string;
}

export interface ReoccurringTasks {
    id: number;
    plannedDate: string; // ISO 8601 date-time string
    template: ReoccurringTaskTemplate;
    assingnee: Assignee;
    isPerformed: boolean;
    extendedName: string | null;
    performedBy: string | null;
    performedAt: string | null; // ISO 8601 date-time string (when not null)
}

interface ReoccurringTasksState {
    reoccurringTasks: ReoccurringTasks[];
    ownReoccurringTasks: ReoccurringTasks[];
    historyReoccurringTasks: ReoccurringTasks[];
    taskTemplates: ReoccurringTaskTemplate[];
}

export interface ReoccurringTaskTemplate {
    id: number;
    name: string;
    reoccurringType: number;
    standardTime: string;
    reportBackRequired: boolean;
}

export const useReoccurringTasksStore = defineStore('ReoccurringTasks', {
    state: (): ReoccurringTasksState => ({
        reoccurringTasks: [],
        ownReoccurringTasks: [],
        historyReoccurringTasks: [],
        taskTemplates: []
    }),
    getters: {
        getAllReoccurringTasks: (state): ReoccurringTasks[] => {
            return state.reoccurringTasks;
        },
        getOwnReocurringTasks: (state): ReoccurringTasks[] => {
            return state.ownReoccurringTasks;
        },
        getTaskTemplateList: (state): ReoccurringTaskTemplate[] => {
            return state.taskTemplates;
        },
    },
    actions: {
        async getAllReoccurringtasks(): Promise<ReoccurringTasks[]> {
            try {
                const response = await jwtInterceptor.get<ReoccurringTasks[]>('v1/reoccurring-task/');
                this.reoccurringTasks = response.data;
                return response.data;
            } catch (error: unknown) { // Change to unknown
                if (error instanceof AxiosError) { // Type guard to check if it's an AxiosError
                    console.error('Error fetching own reoccurring tasks:', error.message);
                } else {
                    console.error('An unexpected error occurred:', error);
                }
                return [];
            }
        },
        async createTaskTemplate(
            name: string,
            reoccuringType: number,
            standardTime: string,
            reportBackRequired: boolean
        ): Promise<void> {
            await jwtInterceptor.post('v1/reoccurring-task/template', {
                name,
                reoccuringType,
                standardTime,
                reportBackRequired,
            });
        },
        /**
         * DEVAMS-2479: fetch all task templates for the authenticated user's
         * station (server-scoped, soft-deleted templates excluded).
         */
        async getTaskTemplates(): Promise<ReoccurringTaskTemplate[]> {
            try {
                const response = await jwtInterceptor.get<ReoccurringTaskTemplate[]>('v1/reoccurring-task/template');
                this.taskTemplates = response.data;
                return response.data;
            } catch (error: unknown) {
                if (error instanceof AxiosError) {
                    console.error('Error fetching task templates:', error.message);
                } else {
                    console.error('An unexpected error occurred:', error);
                }
                return [];
            }
        },
        /**
         * DEVAMS-2479: soft-delete (disable) a task template. Throws on
         * failure so the caller can surface a snackbar/error message.
         */
        async deleteTaskTemplate(id: number): Promise<void> {
            await jwtInterceptor.delete(`v1/reoccurring-task/template/${id}`);
            this.taskTemplates = this.taskTemplates.filter(template => template.id !== id);
        },
        async loadOwnReocurringTasks(silent = false): Promise<ReoccurringTasks[]> {
            if (!silent) {
                emitter.emit('loading', true);
            }
            try {
                const response = await jwtInterceptor.get<ReoccurringTasks[]>('v1/reoccurring-task/own');
                this.ownReoccurringTasks = response.data;
                if (!silent) {
                    emitter.emit('loading', false);
                }
                return response.data;
            } catch (error: unknown) { // Change to unknown
                if (!silent) {
                    emitter.emit('loading', false);
                }
                if (error instanceof AxiosError) { // Type guard to check if it's an AxiosError
                    console.error('Error fetching own reoccurring tasks:', error.message);
                } else {
                    console.error('An unexpected error occurred:', error);
                }
                return [];
            }
        },
    },
});
